//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  PurchaseOption,
  PurchaseOptionUsage,
  SamplingDeliveryMethod,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { DATE_FORMAT_MMM_DD_YYYY } from '@campaigns/constants/DateConstants';
import {
  getContentTypesLabel,
  getExpiryBadgeText,
  getExpiryBadgeType,
} from '@campaigns/utils/sampling/samplingCampaignsPurchaseOptionsUtils';
import { Badge, BadgeType } from '@components/atoms/badge/Badge';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { BodyText, CaptionText } from '@components/atoms/text/Text';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import { TableDataCell } from '@components/organisms/table/TableDataCell';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

import { PrepaidPurchaseOptionsTooltipContent } from './PrepaidPurchaseOptionsTooltipContent';

interface PrepaidPurchaseOptionsTableRowContentProps {
  purchaseOption: PurchaseOption;
}

const formatDate = (date: Date) => {
  return moment(date).format(DATE_FORMAT_MMM_DD_YYYY);
};

export const PrepaidPurchaseOptionsTableRowContent = ({
  purchaseOption,
}: PrepaidPurchaseOptionsTableRowContentProps) => {
  const timeZone = useSelector(getUserTimeZone);

  const expiryBadgeInfo = isDefined(purchaseOption.expiryDate)
    ? getExpiryBadgeType(purchaseOption.expiryDate, timeZone)
    : undefined;

  const usageLabel = purchaseOption.areUnitsFlexible
    ? PurchaseOptionUsage.MULTI_USE
    : PurchaseOptionUsage.SINGLE_USE;

  const contentTypesLabel = getContentTypesLabel(purchaseOption);

  const showViewDetails =
    purchaseOption.deliveryMethod ===
    SamplingDeliveryMethod.ShippedByBazaarvoice;

  const fulfillmentOptionsCount =
    purchaseOption.fulfillmentOptions?.length ?? 0;

  return (
    <>
      <TableDataCell>
        <CellContainer>
          <BodyText>{purchaseOption.opportunityId}</BodyText>
          <CaptionText>{purchaseOption.opportunityRef}</CaptionText>
        </CellContainer>
      </TableDataCell>
      <TableDataCell>
        <CellContainer>
          <BodyText>
            {(purchaseOption.unitCount ?? 0).toLocaleString()} units
          </BodyText>
          <CaptionText>{contentTypesLabel}</CaptionText>
        </CellContainer>
      </TableDataCell>
      <TableDataCell>
        <CellContainer>
          <BodyText>{purchaseOption.deliveryMethod}</BodyText>
          {showViewDetails && (
            <>
              <CaptionText>
                {pluralize(fulfillmentOptionsCount, 'Option')} Available
              </CaptionText>
              <TooltipContainer>
                {purchaseOption.fulfillmentOptions && (
                  <MediumTooltip
                    content={
                      <PrepaidPurchaseOptionsTooltipContent
                        fulfillmentOptions={purchaseOption.fulfillmentOptions}
                      />
                    }
                    placement='bottom'
                    target={<PrimaryLinkButton>View Details</PrimaryLinkButton>}
                  />
                )}
              </TooltipContainer>
            </>
          )}
        </CellContainer>
      </TableDataCell>
      <TableDataCell>
        <Badge size='small' text={usageLabel} type={BadgeType.Neutral} />
      </TableDataCell>
      <TableDataCell>
        <CellContainer>
          <BodyText>
            {isDefined(purchaseOption.expiryDate)
              ? formatDate(purchaseOption.expiryDate)
              : 'N/A'}
          </BodyText>
          {expiryBadgeInfo && (
            <Badge
              size='small'
              text={getExpiryBadgeText(expiryBadgeInfo.daysUntilExpiry)}
              type={expiryBadgeInfo.type}
            />
          )}
        </CellContainer>
      </TableDataCell>
    </>
  );
};

const CellContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const TooltipContainer = styled.div`
  align-self: flex-start;
`;
